import { useMutation, useQueryClient } from "@tanstack/react-query";
import React from "react";
import { auth } from "../../../../../core/auth";
import useApi from "../../../../../shared/hooks/useApi";
import { useIndexedDraftList } from "../../../../entityProfileModal/hooks/useIndexedDraftList";
import useEntityProfileToast from "../../../../entityProfileModal/hooks/useEntityProfileToast";
import type { CaregiverProfileCaregiver } from "../../../caregiverProfileModal.types";
import type {
  CaregiverEmergencyContact,
  UpdateCaregiverAgencyAssocBody,
} from "./caregiverEmergencyContact.types";
import {
  createEmptyCaregiverEmergencyContact,
  getCaregiverEmergencyContactsValidationMessage,
  mapCaregiverEmergencyContactToDraft,
  normalizeCaregiverEmergencyContact,
} from "./caregiverEmergencyContact.utils";

export function useCaregiverProfileEmergencyContactController(
  caregiver: CaregiverProfileCaregiver
) {
  const { api } = useApi();
  const queryClient = useQueryClient();
  const toast = useEntityProfileToast();
  const canEdit = auth.isAllowedTo("edit_caregiver_page_emergency");
  const emergencyContacts = React.useMemo(
    () => caregiver.emergencyContacts ?? [],
    [caregiver.emergencyContacts]
  );
  const draftEmergencyContacts = useIndexedDraftList<CaregiverEmergencyContact>();
  const resetDraftEmergencyContacts = draftEmergencyContacts.resetItems;
  const [isEditing, setIsEditing] = React.useState(false);
  const [validationMessages, setValidationMessages] = React.useState<string[] | null>(null);

  React.useEffect(() => {
    if (isEditing) {
      return;
    }

    resetDraftEmergencyContacts(emergencyContacts.map(mapCaregiverEmergencyContactToDraft));
  }, [emergencyContacts, isEditing, resetDraftEmergencyContacts]);

  const updateEmergencyContactsMutation = useMutation({
    mutationFn: (body: UpdateCaregiverAgencyAssocBody) =>
      api.put("./caregivers/:caregiverId/agency_assoc", {
        path: { caregiverId: caregiver.id },
        body,
      }),
    onSuccess: (_, body) => {
      queryClient.setQueryData(["caregiver", "get", caregiver.id], (currentCaregiver: unknown) => {
        if (
          currentCaregiver === undefined ||
          currentCaregiver === null ||
          typeof currentCaregiver !== "object"
        ) {
          return currentCaregiver;
        }

        return {
          ...currentCaregiver,
          emergencyContacts: body.emergencyContacts ?? [],
        };
      });
      setIsEditing(false);
      setValidationMessages(null);
      toast({
        description: "Caregiver emergency contacts updated successfully.",
        status: "success",
      });
    },
    onError: () => {
      toast({
        description: "Could not update caregiver emergency contacts.",
        status: "error",
      });
    },
  });

  const startEditing = () => {
    resetDraftEmergencyContacts(emergencyContacts.map(mapCaregiverEmergencyContactToDraft));
    setValidationMessages(null);
    setIsEditing(true);
  };

  const cancelEditing = () => {
    resetDraftEmergencyContacts(emergencyContacts.map(mapCaregiverEmergencyContactToDraft));
    setValidationMessages(null);
    setIsEditing(false);
  };

  const saveEmergencyContacts = () => {
    const nextValidationMessages = getCaregiverEmergencyContactsValidationMessage(
      draftEmergencyContacts.items
    );

    if (nextValidationMessages !== null) {
      setValidationMessages(nextValidationMessages);
      return;
    }

    updateEmergencyContactsMutation.mutate({
      emergencyContacts: draftEmergencyContacts.items.map(normalizeCaregiverEmergencyContact),
    });
  };

  return {
    addEmergencyContact: () => draftEmergencyContacts.addItem(createEmptyCaregiverEmergencyContact),
    canEdit,
    cancelEditing,
    draftEmergencyContacts: draftEmergencyContacts.items,
    emergencyContacts,
    isEditing,
    isSaving: updateEmergencyContactsMutation.isPending,
    removeEmergencyContact: draftEmergencyContacts.removeItem,
    saveEmergencyContacts,
    showErrors: validationMessages !== null,
    startEditing,
    updateEmergencyContact: draftEmergencyContacts.updateItem,
    validationMessages,
  };
}
